<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { View16, ViewOff16 } from '@carbon/icons-vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConfirmButton from 'dashboard/components-next/button/ConfirmButton.vue';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from 'dashboard/components-next/polaris/ui/input-group';

const props = defineProps({
  value: { type: String, default: '' },
  showResetButton: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['onCopy', 'onReset']);

// Phase 9.5: a read-only field of the kit (InputGroup) with the show/hide toggle inside it, then Copy and
// "Generate new" (a confirm button, as before). Same props and events; the bot drawer uses it too.
const { t } = useI18n();

const inputType = ref('password');

const toggleMasked = () => {
  inputType.value = inputType.value === 'password' ? 'text' : 'password';
};

const isMasked = computed(() => inputType.value === 'password');

const onClick = () => {
  emit('onCopy', props.value);
};

const onReset = () => {
  emit('onReset');
};
</script>

<template>
  <div class="flex flex-wrap items-center gap-2">
    <InputGroup class="min-w-0 flex-1 basis-56">
      <InputGroupInput
        name="access_token"
        :type="inputType"
        :model-value="value"
        :disabled="disabled"
        readonly
        class="font-mono text-polaris-muted-foreground"
      />
      <InputGroupAddon align="inline-end">
        <InputGroupButton
          size="icon-xs"
          :disabled="disabled"
          :aria-label="t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.TITLE')"
          :aria-pressed="!isMasked"
          @click="toggleMasked"
        >
          <View16 v-if="isMasked" aria-hidden="true" />
          <ViewOff16 v-else aria-hidden="true" />
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
    <div class="flex flex-row gap-2">
      <NextButton
        :label="t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.COPY')"
        slate
        outline
        sm
        type="button"
        icon="i-lucide-copy"
        :disabled="disabled"
        @click="onClick"
      />
      <ConfirmButton
        v-if="showResetButton"
        :label="t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.RESET')"
        :confirm-label="t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.CONFIRM_RESET')"
        :confirm-hint="t('PROFILE_SETTINGS.FORM.ACCESS_TOKEN.CONFIRM_HINT')"
        color="slate"
        confirm-color="ruby"
        variant="outline"
        size="sm"
        icon="i-lucide-key-round"
        :disabled="disabled"
        @click="onReset"
      />
    </div>
  </div>
</template>
